import { where, type QueryConstraint } from "firebase/firestore";
import {
  where as whereLite,
  type QueryConstraint as LiteQueryConstraint,
} from "firebase/firestore/lite";
import { firebaseHelper, getActiveDesignFirmId } from "ui";
import { sharingV2Enabled, sharingV2Known } from "ui/utils/workspace/sharingV2";
import { ensureSharingV2Flag } from "ui/utils/useDesignFirm";
import {
  ACCESS_KINDS,
  teamReaderKey,
  type AccessKind,
  type TeamListFilter,
} from "@mattoboard/schemas";

// The one query shape for every team-workspace list under the Teams sharing
// model: design_firm_id == X AND readers array-contains-any [uid, team:X].
// Personal lists keep their owner-scoped queries.

export interface TeamScope {
  uid: string;
  firmId: string;
}

// Non-null only in a team workspace whose sharingV2 flag is on.
export const activeTeamScope = (
  uid: string | null | undefined,
): TeamScope | null => {
  if (!uid) return null;
  const firmId = getActiveDesignFirmId(uid);
  return firmId && sharingV2Enabled(firmId) ? { uid, firmId } : null;
};

// True once the active team's flag is known (or there is no active team).
export const teamScopeKnown = (uid: string | null | undefined) => {
  const firmId = uid ? getActiveDesignFirmId(uid) : null;
  return !firmId || sharingV2Known(firmId);
};

// The same, after the active team's flag is known: on a cold load it is read
// once (a failed read settles and the legacy query runs), so a list's first
// query takes the right shape instead of painting the legacy one.
export const awaitActiveTeamScope = async (
  uid: string | null | undefined,
): Promise<TeamScope | null> => {
  const firmId = uid ? getActiveDesignFirmId(uid) : null;
  if (firmId && !sharingV2Known(firmId)) {
    await ensureSharingV2Flag(firebaseHelper.db, firmId);
  }
  return activeTeamScope(uid);
};

// The scope for one project's items: a project outside the active team
// (Personal, another team) keeps its legacy query; an unknown project takes
// the active scope.
export const projectTeamScope = (
  uid: string | null | undefined,
  project: { design_firm_id?: string | null } | null | undefined,
): TeamScope | null => {
  const scope = activeTeamScope(uid);
  if (!scope || !project) return scope;
  return project.design_firm_id === scope.firmId ? scope : null;
};

export const teamReaders = ({ uid, firmId }: TeamScope): string[] => [
  uid,
  teamReaderKey(firmId),
];

export const teamScopeConstraints = (scope: TeamScope): QueryConstraint[] => [
  where("design_firm_id", "==", scope.firmId),
  where("readers", "array-contains-any", teamReaders(scope)),
];

export const teamScopeConstraintsLite = (
  scope: TeamScope,
): LiteQueryConstraint[] => [
  whereLite("design_firm_id", "==", scope.firmId),
  whereLite("readers", "array-contains-any", teamReaders(scope)),
];

// The same two clauses for the REST runQuery body (firestoreMaskedQuery).
export const teamScopeRestFilters = (scope: TeamScope) => [
  {
    fieldFilter: {
      field: { fieldPath: "design_firm_id" },
      op: "EQUAL",
      value: { stringValue: scope.firmId },
    },
  },
  {
    fieldFilter: {
      field: { fieldPath: "readers" },
      op: "ARRAY_CONTAINS_ANY",
      value: {
        arrayValue: {
          values: teamReaders(scope).map((stringValue) => ({ stringValue })),
        },
      },
    },
  },
];

// The owner clause for the REST body, outside a team scope (Personal is
// owner-only under the rules).
export const ownerRestFilter = (field: string, uid: string) => [
  { fieldFilter: { field: { fieldPath: field }, op: "EQUAL", value: { stringValue: uid } } },
];

// Firestore caps a query at 30 disjunctions: in[15] × array-contains-any[2].
export const TEAM_IN_CHUNK = 15;
export const LEGACY_IN_CHUNK = 30;
export const inChunkSize = (scope: TeamScope | null | undefined) =>
  scope ? TEAM_IN_CHUNK : LEGACY_IN_CHUNK;

export const chunkIds = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export const TEAM_LIST_FILTERS: { value: TeamListFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "created_by_me", label: "Created by me" },
  { value: "shared_with_me", label: "Shared with me" },
];

// Applied to the loaded page: Created by me = (created_by ?? owner) == uid,
// Shared with me = the rest.
export function applyTeamListFilter<T extends object>(
  kind: AccessKind,
  items: T[],
  filter: TeamListFilter,
  uid: string,
): T[] {
  if (filter === "all") return items;
  const { ownerField } = ACCESS_KINDS[kind];
  const mine = (item: T) => {
    const d = item as Record<string, unknown>;
    return (d.created_by || d[ownerField]) === uid;
  };
  return items.filter((item) => (filter === "created_by_me") === mine(item));
}
